const ACTIVE_PROJECT_KEY = "fastblog_active_project";
const ACTIVE_TENANT_KEY = "fastblog_active_tenant";
const CONTEXT_USER_KEY = "fastblog_context_user";
const IMPERSONATION_KEY = "fastblog_admin_impersonating";

export interface TenantImpersonation {
  adminUserId: string;
  companyId: string;
  companyName: string;
  projectId: string | null;
  returnTo: string;
}

export function clearTenantContext() {
  try {
    sessionStorage.removeItem(ACTIVE_PROJECT_KEY);
    sessionStorage.removeItem(ACTIVE_TENANT_KEY);
    sessionStorage.removeItem(CONTEXT_USER_KEY);
    sessionStorage.removeItem(IMPERSONATION_KEY);
    sessionStorage.removeItem("fastblog_company_role");
    sessionStorage.removeItem("fastblog_subscription_cache");
  } catch {
    /* ignore unavailable storage */
  }
}

export function ensureTenantContextOwner(userId: string): boolean {
  try {
    const owner = sessionStorage.getItem(CONTEXT_USER_KEY);
    if (owner === userId) return true;
    clearTenantContext();
    sessionStorage.setItem(CONTEXT_USER_KEY, userId);
    return false;
  } catch {
    return false;
  }
}

export function getStoredTenantContext(userId: string) {
  ensureTenantContextOwner(userId);
  try {
    return {
      projectId: sessionStorage.getItem(ACTIVE_PROJECT_KEY),
      companyId: sessionStorage.getItem(ACTIVE_TENANT_KEY),
    };
  } catch {
    return { projectId: null, companyId: null };
  }
}

export function setStoredTenantContext(
  userId: string,
  context: { projectId?: string | null; companyId?: string | null },
) {
  ensureTenantContextOwner(userId);
  try {
    sessionStorage.setItem(CONTEXT_USER_KEY, userId);
    if (context.projectId) sessionStorage.setItem(ACTIVE_PROJECT_KEY, context.projectId);
    else if (context.projectId === null) sessionStorage.removeItem(ACTIVE_PROJECT_KEY);
    if (context.companyId) sessionStorage.setItem(ACTIVE_TENANT_KEY, context.companyId);
    else if (context.companyId === null) sessionStorage.removeItem(ACTIVE_TENANT_KEY);
  } catch {
    /* ignore unavailable storage */
  }
}

export function getTenantImpersonation(userId: string): TenantImpersonation | null {
  ensureTenantContextOwner(userId);
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TenantImpersonation>;
    if (parsed.adminUserId !== userId || !parsed.companyId || !parsed.returnTo) {
      sessionStorage.removeItem(IMPERSONATION_KEY);
      return null;
    }
    return parsed as TenantImpersonation;
  } catch {
    return null;
  }
}

export function setTenantImpersonation(userId: string, value: Omit<TenantImpersonation, "adminUserId">) {
  ensureTenantContextOwner(userId);
  try {
    sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify({ ...value, adminUserId: userId }));
  } catch {
    /* ignore unavailable storage */
  }
}

export function clearTenantImpersonation() {
  try {
    sessionStorage.removeItem(IMPERSONATION_KEY);
  } catch {
    /* ignore unavailable storage */
  }
}